import React, { FC, useEffect, useState } from 'react';
import { InputAdornment, SxProps, Theme } from '@mui/material';
import GenericTextField from './GenericTextField/GenericTextField';
import { DEFAULT_CURRENCY, formatMoney, parseMoneyInput, toMoneyInputValue } from '../utilities/helper/money.helper';
import { useTranslation } from 'react-i18next';

interface MoneyFieldProps {
  label: string;
  value: number | null;
  onChange: (value: number | null) => void;
  disabled?: boolean;
  required?: boolean;
  currency?: string;
  error?: boolean;
  helperText?: string;
  sx?: SxProps<Theme>;
}

/**
 * Amount input.
 *
 * Keeps the typed text in local state and only reformats on blur. Reformatting while
 * typing moves the caret and makes the field feel broken as soon as a thousands
 * separator appears.
 */
const MoneyField: FC<MoneyFieldProps> = ({
  label,
  value,
  onChange,
  disabled,
  required,
  currency = DEFAULT_CURRENCY,
  error,
  helperText,
  sx
}) => {
  const { t } = useTranslation();
  const [text, setText] = useState<string>(value === null ? '' : formatMoney(value));
  const [focused, setFocused] = useState(false);
  const [invalid, setInvalid] = useState(false);

  // follow external changes (loading a record, resetting a form) unless being edited
  useEffect(() => {
    if (!focused) {
      setText(value === null || value === undefined ? '' : formatMoney(value));
      setInvalid(false);
    }
  }, [value, focused]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const next = event.target.value;
    if (next === '' || /^[\d.,\s-]*$/.test(next)) {
      setText(next);
    }
  };

  const handleFocus = () => {
    setFocused(true);
    setText(toMoneyInputValue(value));
  };

  const handleBlur = () => {
    setFocused(false);
    const parsed = parseMoneyInput(text);
    if (parsed === undefined) {
      setInvalid(true);
      return;
    }
    setInvalid(false);
    onChange(parsed);
    setText(parsed === null ? '' : formatMoney(parsed));
  };

  return (
    <GenericTextField
      label={label}
      value={text}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      disabled={disabled}
      required={required}
      error={error || invalid}
      helperText={invalid ? t('money.invalidAmount') : helperText}
      sx={sx}
      inputProps={{ inputMode: 'decimal', autoComplete: 'off', style: { textAlign: 'right' } }}
      InputProps={{
        endAdornment: <InputAdornment position="end">{currency}</InputAdornment>
      }}
    />
  );
};

export default MoneyField;
